import React, { useCallback, useEffect, useState } from 'react';
import { Row, Col, Card, Button, Spinner, Alert, Badge, Form } from 'react-bootstrap';
import {
  ocipIpAllowlistService, publicApiKeyService, scimApiKeyService, lockedSignupDomainService, signupDomainService,
} from '../../services/apiService';
import StackedTable from '../StackedTable';
import { useConfirm } from '../ConfirmDialog';
import { ErrorState, LoadingState, serverMessage } from '../PageState';
import SaveBar from './SaveBar';
import { useCompanyForm, useCompanyList } from './companyHooks';
import { EMPTY, formatDate, formatDateTime, formatNumber, statusLabel } from '../../utils/format';

const IP_ADDRESS_STATUS_LABELS = { PENDING: 'Pending approval', APPROVED: 'Approved', REJECTED: 'Rejected' };
const IGNORED_CHECKIN_REASONS = {
  MISSING_FIELDS: 'Missing details',
  UNKNOWN_PERSON: 'Person not found',
  MAC_IN_TWO_COMPANIES: 'MAC address in two companies',
  NETWORK_NOT_MAPPED: 'Network not mapped',
};
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const statusBg = (status) => (status === 'APPROVED' ? 'success' : status === 'PENDING' ? 'warning' : 'secondary');

// A plain date such as "2026-08-01" is read as local midnight, so it never shifts a day
const formatDay = (day) => (day ? formatDate(`${day}T00:00:00`) : EMPTY);

// Each form's own fields, as its inputs expect them (module level, so they stay stable)
const toNameSignInDraft = (c) => ({
  name: c.name || '',
  azureTenantId: c.azureTenantId || '',
  tenantBasedAuthEnabled: !!c.tenantBasedAuthEnabled,
});
const toSignupDraft = (c) => ({ domainNameSignupsEnabled: !!c.domainNameSignupsEnabled });
const toLimitsDraft = (c) => ({
  numberOfTeamsLimit: c.numberOfTeamsLimit || 0,
  numberOfTagsLimit: c.numberOfTagsLimit || 0,
  meetingRoomLimit: c.meetingRoomLimit || 0,
});
const toScimDraft = (c) => ({ deleteWhenSoftDeleted: !!c.deleteWhenSoftDeleted });
const toCheckinDraft = (c) => ({ automaticOfficeCheckinEnabled: !!c.automaticOfficeCheckinEnabled });
const toPublicApiDraft = (c) => ({ publicApiAccessEnabled: !!c.publicApiAccessEnabled });

const limitValue = (value) => {
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? 0 : Math.max(0, parsed);
};

/** A section of Config as its own card; id is where the overview's links scroll to. */
const ConfigCard = ({ id, title, children }) => (
  <Card id={id} className="shadow-sm mb-4" style={{ scrollMarginTop: 120 }}>
    <Card.Header className="bg-light">
      <h5 className="mb-0">{title}</h5>
    </Card.Header>
    <Card.Body>{children}</Card.Body>
  </Card>
);

/** One settings form: fields disabled for view-only users, and a Save that sends only these fields. */
const SettingsForm = ({ form, canEdit, invalid = false, children }) => (
  <Form
    noValidate
    onSubmit={(e) => {
      e.preventDefault();
      if (canEdit && form.dirty && !invalid && !form.saving) form.save();
    }}
  >
    <fieldset disabled={!canEdit}>{children}</fieldset>
    {canEdit && <SaveBar dirty={form.dirty} saving={form.saving} status={form.status} disabled={invalid} />}
  </Form>
);

const SwitchField = ({ id, label, help, checked, onChange, disabled }) => (
  <Form.Group className="mb-0">
    <Form.Check
      type="switch"
      id={id}
      label={label}
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      aria-describedby={help ? `${id}-help` : undefined}
    />
    {help && <Form.Text id={`${id}-help`} className="text-muted d-block">{help}</Form.Text>}
  </Form.Group>
);

/** A button that keeps its name while busy, so screen readers never announce an empty button. */
const BusyButton = ({ busy, busyLabel, children, ...props }) => (
  <Button {...props}>
    {busy && <Spinner as="span" animation="border" size="sm" aria-hidden="true" className="me-2" />}
    {busy ? busyLabel : children}
  </Button>
);

/** A list section's loading, error and empty states, so a failed load never reads as "none yet". */
const ListState = ({ list, loadingLabel, errorText, emptyText, children }) => {
  if (list.error) return <ErrorState message={errorText} onRetry={list.reload} className="mb-3" />;
  if (!list.loaded) return <LoadingState label={loadingLabel} className="py-3" />;
  if (list.data.length === 0) return <p className="text-muted mb-3">{emptyText}</p>;
  return children;
};

/** An API key shown by its last four characters; admins can copy or reveal it. */
const MaskedKey = ({ value, canReveal }) => {
  const [revealed, setRevealed] = useState(false);
  const [copyState, setCopyState] = useState(null);
  const text = String(value || '');
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyState('Copied');
    } catch {
      setCopyState('Could not copy');
    }
    setTimeout(() => setCopyState(null), 2000);
  };
  return (
    <div className="d-flex flex-wrap align-items-center column-gap-3 row-gap-1">
      <code className="text-break">{revealed ? text : `•••• ${text.slice(-4)}`}</code>
      {canReveal && (
        <span className="d-inline-flex gap-3">
          <Button variant="link" size="sm" className="p-0" onClick={copy}>{copyState || 'Copy'}</Button>
          <Button variant="link" size="sm" className="p-0" onClick={() => setRevealed((r) => !r)}>
            {revealed ? 'Hide' : 'Reveal'}
          </Button>
        </span>
      )}
    </div>
  );
};

/** Sign-up or locked domains: an add field and a table, each change saved as soon as it is made. */
const DomainList = ({
  idPrefix, list, canEdit, busy, error, onDismissError, confirmAdd, onAdd, onApprove, onReject, onRemove,
  addLabel, addButtonLabel, loadingLabel, errorText, emptyText,
}) => {
  const [newDomain, setNewDomain] = useState('');
  const [adding, setAdding] = useState(false);

  const add = async () => {
    const domain = newDomain.trim();
    if (!domain || adding) return;
    if (confirmAdd && !(await confirmAdd(domain))) return;
    setAdding(true);
    const added = await onAdd(domain);
    setAdding(false);
    if (added) setNewDomain('');
  };

  const isBusy = (row, action) => busy?.id === row.id && (!action || busy.action === action);

  return (
    <>
      {canEdit && (
        <Row className="mb-3 align-items-end gy-2">
          <Col md={6}>
            <Form.Group controlId={`${idPrefix}NewDomain`}>
              <Form.Label>{addLabel}</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. acme.com"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
                disabled={adding}
                aria-describedby={`${idPrefix}NewDomainHelp`}
              />
            </Form.Group>
          </Col>
          <Col md="auto">
            <BusyButton variant="outline-primary" onClick={add} disabled={adding || !newDomain.trim()} busy={adding} busyLabel="Adding...">
              {addButtonLabel}
            </BusyButton>
          </Col>
          <Col xs={12} className="mt-1">
            <Form.Text id={`${idPrefix}NewDomainHelp`} className="text-muted">
              Added here as approved immediately. Must not be registered to another company.
            </Form.Text>
          </Col>
        </Row>
      )}
      {error && (
        <Alert variant="danger" dismissible onClose={onDismissError} className="mb-3">{error}</Alert>
      )}
      <ListState list={list} loadingLabel={loadingLabel} errorText={errorText} emptyText={emptyText}>
        <StackedTable size="sm" responsive className="mb-3">
          <thead>
            <tr>
              <th>Domain</th>
              <th>Status</th>
              <th>Matching users</th>
              <th className="d-md-none d-xl-table-cell">Requested</th>
              <th className="d-md-none d-xl-table-cell">Decided</th>
              {canEdit && <th><span className="visually-hidden">Actions</span></th>}
            </tr>
          </thead>
          <tbody>
            {list.data.map((row) => (
              <tr key={row.id}>
                <td>
                  <code>{row.domain}</code>
                  {row.conflict && (
                    <Badge bg="warning" className="ms-2 text-wrap text-start" title={row.conflict}>Conflict: {row.conflict}</Badge>
                  )}
                </td>
                <td><Badge bg={statusBg(row.status)}>{statusLabel(row.status)}</Badge></td>
                <td>
                  {formatNumber(row.matchingUsers)}
                  {row.matchingUsers === 0 && <Badge bg="warning" className="ms-2">Check ownership</Badge>}
                </td>
                <td className="d-md-none d-xl-table-cell text-nowrap">{formatDate(row.createdAt)}</td>
                <td className="d-md-none d-xl-table-cell">
                  <span className="text-nowrap">{formatDate(row.decidedAt)}</span>
                  {row.decidedBy && <span className="text-muted small"> by {row.decidedBy}</span>}
                </td>
                {canEdit && (
                  <td className="text-end text-nowrap">
                    {row.status !== 'APPROVED' && (
                      <BusyButton
                        variant="outline-primary"
                        size="sm"
                        className="me-2"
                        disabled={isBusy(row)}
                        busy={isBusy(row, 'approve')}
                        busyLabel="Approving..."
                        onClick={() => onApprove(row)}
                      >
                        Approve
                      </BusyButton>
                    )}
                    {row.status !== 'REJECTED' && (
                      <BusyButton
                        variant="outline-danger"
                        size="sm"
                        className="me-2"
                        disabled={isBusy(row)}
                        busy={isBusy(row, 'reject')}
                        busyLabel="Rejecting..."
                        onClick={() => onReject(row)}
                      >
                        Reject
                      </BusyButton>
                    )}
                    <BusyButton
                      variant="outline-secondary"
                      size="sm"
                      disabled={isBusy(row)}
                      busy={isBusy(row, 'remove')}
                      busyLabel="Removing..."
                      onClick={() => onRemove(row)}
                    >
                      Remove
                    </BusyButton>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </StackedTable>
      </ListState>
    </>
  );
};

/** "1 of the company's users already has this domain." */
const matchingUsersText = (row) => {
  if (!row.matchingUsers) return 'None of the company\'s users have this domain yet, so check the company owns it before approving.';
  return row.matchingUsers === 1
    ? '1 of the company\'s users already has this domain.'
    : `${formatNumber(row.matchingUsers)} of the company's users already have this domain.`;
};

const conflictWarning = (row) => row.conflict && (
  <Alert variant="warning" className="mt-3 mb-0">
    Conflict: {row.conflict}. Approving over a conflict is almost always wrong.
  </Alert>
);

/**
 * The Config section. Each card saves its own settings with its own Save, and the lists inside
 * the cards (domains, IP addresses, API keys) save each change as it is made. Parts that a switch
 * shows or hides follow the saved setting, not an unsaved flip of the switch.
 *
 * lists holds the page-level lists this section shares with the overview and header:
 * { signupDomains, lockedSignupDomains, scimApiKeys, ipAddresses, offices }.
 */
const CompanyConfig = ({ companyId, company, canEdit, api, onSaveCompany, onDirtyChange, lists }) => {
  const { signupDomains, lockedSignupDomains, scimApiKeys, ipAddresses, offices } = lists;
  const { confirm, confirmDialog } = useConfirm();
  const companyName = company.name || 'this company';

  const formOptions = { company, onSaveCompany, onDirtyChange };
  const nameSignInForm = useCompanyForm({ ...formOptions, formKey: 'config-signin', toDraft: toNameSignInDraft });
  const signupForm = useCompanyForm({ ...formOptions, formKey: 'config-signup', toDraft: toSignupDraft });
  const limitsForm = useCompanyForm({ ...formOptions, formKey: 'config-limits', toDraft: toLimitsDraft });
  const scimForm = useCompanyForm({ ...formOptions, formKey: 'config-scim', toDraft: toScimDraft });
  const checkinForm = useCompanyForm({ ...formOptions, formKey: 'config-checkin', toDraft: toCheckinDraft });
  const publicApiForm = useCompanyForm({ ...formOptions, formKey: 'config-public-api', toDraft: toPublicApiDraft });

  // Lists only this section shows
  const unlistedIps = useCompanyList(useCallback(
    async () => (await api(() => ocipIpAllowlistService.unlisted(companyId))).data, [api, companyId]));
  const ignoredCheckins = useCompanyList(useCallback(
    async () => (await api(() => ocipIpAllowlistService.ignored(companyId))).data, [api, companyId]));
  const publicApiKeys = useCompanyList(useCallback(
    async () => (await api(() => publicApiKeyService.getApiKeys(companyId))).data, [api, companyId]));

  const checkinOn = !!company.automaticOfficeCheckinEnabled;
  // The admin API has not always returned this setting; without it the switch cannot be read or saved
  const publicApiFlagKnown = Object.prototype.hasOwnProperty.call(company, 'publicApiAccessEnabled');
  const publicApiOn = !!company.publicApiAccessEnabled;

  const reloadPublicApiKeys = publicApiKeys.reload;
  useEffect(() => {
    reloadPublicApiKeys();
  }, [reloadPublicApiKeys]);

  const reloadUnlisted = unlistedIps.reload;
  const reloadIgnored = ignoredCheckins.reload;
  useEffect(() => {
    if (!checkinOn) return;
    reloadUnlisted();
    reloadIgnored();
  }, [checkinOn, reloadUnlisted, reloadIgnored]);

  // ---- Sign-up and locked domains ----

  const [domainBusy, setDomainBusy] = useState({ signup: null, locked: null });
  const [domainErrors, setDomainErrors] = useState({ signup: null, locked: null });
  const setDomainError = (kind, message) => setDomainErrors((prev) => ({ ...prev, [kind]: message }));
  const domainKinds = {
    signup: { service: signupDomainService, list: signupDomains, noun: 'sign-up domain' },
    locked: { service: lockedSignupDomainService, list: lockedSignupDomains, noun: 'locked domain' },
  };

  // A sign-up domain lets people straight into the account, so adding one is confirmed first
  const confirmAddSignupDomain = (domain) => confirm({
    title: `Add ${domain} as a sign-up domain?`,
    body: `It is approved straight away. Anyone who signs up with an @${domain} email will join ${companyName} without an invitation, so check the company owns the domain first.`,
    confirmLabel: 'Add domain',
  });

  const addDomain = async (kind, domain) => {
    const { service, list, noun } = domainKinds[kind];
    try {
      setDomainError(kind, null);
      const response = await api(() => service.add(companyId, domain));
      list.setData((prev) => [...prev, response.data]);
      return true;
    } catch (err) {
      setDomainError(kind, serverMessage(err, err.message || `Could not add the ${noun}`));
      console.error(err);
      return false;
    }
  };

  const decideDomain = async (kind, row, decision) => {
    const { service, list, noun } = domainKinds[kind];
    if (decision === 'approve') {
      const ok = await confirm(kind === 'signup'
        ? {
          title: `Approve ${row.domain}?`,
          body: (
            <>
              <p>Anyone who signs up with an <strong>@{row.domain}</strong> email will join {companyName} straight away, without an invitation.</p>
              <p className="mb-0">{matchingUsersText(row)}</p>
              {conflictWarning(row)}
            </>
          ),
          confirmLabel: 'Approve domain',
        }
        : {
          title: `Approve locked domain ${row.domain}?`,
          body: (
            <>
              <p>New accounts can no longer be created with an <strong>@{row.domain}</strong> email.</p>
              <p className="mb-0">{matchingUsersText(row)}</p>
              {conflictWarning(row)}
            </>
          ),
          confirmLabel: 'Approve domain',
        });
      if (!ok) return;
    }
    try {
      setDomainBusy((prev) => ({ ...prev, [kind]: { id: row.id, action: decision } }));
      setDomainError(kind, null);
      const response = decision === 'approve'
        ? await api(() => service.approve(companyId, row.id))
        : await api(() => service.reject(companyId, row.id));
      list.setData((prev) => prev.map((d) => (d.id === row.id ? response.data : d)));
    } catch (err) {
      setDomainError(kind, serverMessage(err, `Could not ${decision} the ${noun}`));
      console.error(err);
    } finally {
      setDomainBusy((prev) => ({ ...prev, [kind]: null }));
    }
  };

  const removeDomain = async (kind, row) => {
    const { service, list, noun } = domainKinds[kind];
    const ok = await confirm(kind === 'signup'
      ? {
        title: `Remove ${row.domain}?`,
        body: `New sign-ups with an @${row.domain} email will stop joining ${companyName} automatically.`,
        confirmLabel: 'Remove domain',
        variant: 'danger',
      }
      : {
        title: `Remove ${row.domain} from the locked domains?`,
        body: `New accounts can be created with an @${row.domain} email again.`,
        confirmLabel: 'Remove domain',
        variant: 'danger',
      });
    if (!ok) return;
    try {
      setDomainBusy((prev) => ({ ...prev, [kind]: { id: row.id, action: 'remove' } }));
      setDomainError(kind, null);
      await api(() => service.remove(companyId, row.id));
      list.setData((prev) => prev.filter((d) => d.id !== row.id));
    } catch (err) {
      setDomainError(kind, serverMessage(err, `Could not remove the ${noun}`));
      console.error(err);
    } finally {
      setDomainBusy((prev) => ({ ...prev, [kind]: null }));
    }
  };

  const domainListProps = (kind) => ({
    list: domainKinds[kind].list,
    canEdit,
    busy: domainBusy[kind],
    error: domainErrors[kind],
    onDismissError: () => setDomainError(kind, null),
    confirmAdd: kind === 'signup' ? confirmAddSignupDomain : undefined,
    onAdd: (domain) => addDomain(kind, domain),
    onApprove: (row) => decideDomain(kind, row, 'approve'),
    onReject: (row) => decideDomain(kind, row, 'reject'),
    onRemove: (row) => removeDomain(kind, row),
  });

  // ---- SCIM keys ----

  const [unlockingScimKeyId, setUnlockingScimKeyId] = useState(null);
  const [scimError, setScimError] = useState(null);

  const handleUnlockScimApiKey = async (keyId) => {
    try {
      setUnlockingScimKeyId(keyId);
      setScimError(null);
      const response = await api(() => scimApiKeyService.unlockApiKey(companyId, keyId));
      scimApiKeys.setData((prev) => prev.map((k) => (k.id === keyId ? response.data : k)));
    } catch (err) {
      setScimError(serverMessage(err, 'Could not unlock the SCIM API key'));
      console.error(err);
    } finally {
      setUnlockingScimKeyId(null);
    }
  };

  // ---- Office check-in IP addresses ----

  const [newIpAddress, setNewIpAddress] = useState('');
  const [newIpAddressLabel, setNewIpAddressLabel] = useState('');
  const [newIpAddressOfficeId, setNewIpAddressOfficeId] = useState('');
  const [ipAddressCheck, setIpAddressCheck] = useState(null);
  const [isCheckingIpAddress, setIsCheckingIpAddress] = useState(false);
  const [isAddingIpAddress, setIsAddingIpAddress] = useState(false);
  const [ipAddressError, setIpAddressError] = useState(null);
  const [busyIp, setBusyIp] = useState(null);
  // Owner lookups for addresses with no stored owner yet, keyed by row id
  const [ipOwnerChecks, setIpOwnerChecks] = useState({});

  // Add stays disabled until the address currently typed in has been checked
  const ipAddressCheckIsCurrent = ipAddressCheck !== null && ipAddressCheck.input === newIpAddress.trim();

  const handleCheckIpAddress = async () => {
    const ip = newIpAddress.trim();
    if (!ip) return;
    try {
      setIsCheckingIpAddress(true);
      setIpAddressError(null);
      setIpAddressCheck(null);
      const response = await api(() => ocipIpAllowlistService.lookup(companyId, ip));
      setIpAddressCheck({ ...response.data, input: ip });
    } catch (err) {
      setIpAddressError(serverMessage(err, err.message || 'Could not check the IP address'));
      console.error(err);
    } finally {
      setIsCheckingIpAddress(false);
    }
  };

  const handleAddIpAddress = async () => {
    if (!ipAddressCheckIsCurrent) return;
    try {
      setIsAddingIpAddress(true);
      setIpAddressError(null);
      const officeId = newIpAddressOfficeId ? Number(newIpAddressOfficeId) : null;
      const response = await api(() => ocipIpAllowlistService.add(companyId, ipAddressCheck.ipAddress, newIpAddressLabel.trim(), officeId));
      // Re-adding a rejected address replaces the old row
      ipAddresses.setData((prev) => [...prev.filter((r) => r.ipAddress !== response.data.ipAddress), response.data]);
      setNewIpAddress('');
      setNewIpAddressLabel('');
      setNewIpAddressOfficeId('');
      setIpAddressCheck(null);
    } catch (err) {
      setIpAddressError(serverMessage(err, err.message || 'Could not add the IP address'));
      console.error(err);
    } finally {
      setIsAddingIpAddress(false);
    }
  };

  const handleDecideIpAddress = async (row, decision) => {
    if (decision === 'approve' && !(await confirm({
      title: `Approve ${row.ipAddress}${row.officeName ? ` for ${row.officeName}` : ''}?`,
      body: 'Check-ins from this address will be accepted within a minute. Only approve it if it belongs to the customer or their internet provider.',
      confirmLabel: 'Approve address',
    }))) return;
    try {
      setBusyIp({ id: row.id, action: decision });
      setIpAddressError(null);
      const response = decision === 'approve'
        ? await api(() => ocipIpAllowlistService.approve(companyId, row.id))
        : await api(() => ocipIpAllowlistService.reject(companyId, row.id));
      ipAddresses.setData((prev) => prev.map((r) => (r.id === row.id ? response.data : r)));
    } catch (err) {
      setIpAddressError(serverMessage(err, `Could not ${decision} the IP address`));
      console.error(err);
    } finally {
      setBusyIp(null);
    }
  };

  const handleRemoveIpAddress = async (row) => {
    if (!(await confirm({
      title: `Remove ${row.ipAddress}?`,
      body: 'Check-ins from it will stop being accepted within a minute.',
      confirmLabel: 'Remove address',
      variant: 'danger',
    }))) return;
    try {
      setBusyIp({ id: row.id, action: 'remove' });
      setIpAddressError(null);
      await api(() => ocipIpAllowlistService.remove(companyId, row.id));
      ipAddresses.setData((prev) => prev.filter((r) => r.id !== row.id));
    } catch (err) {
      setIpAddressError(serverMessage(err, 'Could not remove the IP address'));
      console.error(err);
    } finally {
      setBusyIp(null);
    }
  };

  const handleCheckIpOwner = async (row) => {
    try {
      setIpOwnerChecks((prev) => ({ ...prev, [row.id]: { loading: true } }));
      const response = await api(() => ocipIpAllowlistService.lookup(companyId, row.ipAddress));
      setIpOwnerChecks((prev) => ({ ...prev, [row.id]: { result: response.data } }));
    } catch (err) {
      setIpOwnerChecks((prev) => ({ ...prev, [row.id]: { error: serverMessage(err, 'Lookup failed') } }));
      console.error(err);
    }
  };

  const ipOtherCompaniesWarning = (otherCompanies) => (
    otherCompanies?.length > 0 && (
      <div>
        <Badge bg="warning" className="mt-1 text-wrap text-start"
               title="A shared address is often a shared building, co-working space or security gateway">
          Also listed for {otherCompanies.map((other) => other.companyName || other.companyId).join(', ')}
        </Badge>
      </div>
    )
  );

  // The stored owner, or a button to look it up for requests that arrive without one
  const renderIpOwner = (row) => {
    const check = ipOwnerChecks[row.id];
    if (row.owner) {
      return <>{row.owner}{ipOtherCompaniesWarning(row.otherCompanies)}</>;
    }
    if (check?.result) {
      return <><strong>{check.result.owner}</strong>{ipOtherCompaniesWarning(check.result.otherCompanies)}</>;
    }
    return (
      <>
        <BusyButton variant="outline-secondary" size="sm" disabled={check?.loading} busy={check?.loading} busyLabel="Checking..." onClick={() => handleCheckIpOwner(row)}>
          Check owner
        </BusyButton>
        {check?.error && <div className="text-danger small">{check.error}</div>}
        {ipOtherCompaniesWarning(row.otherCompanies)}
      </>
    );
  };

  const isIpBusy = (row, action) => busyIp?.id === row.id && (!action || busyIp.action === action);

  // ---- Public API keys ----

  const [isGeneratingPublicApiKey, setIsGeneratingPublicApiKey] = useState(false);
  const [deletingKeyId, setDeletingKeyId] = useState(null);
  const [apiKeyError, setApiKeyError] = useState(null);

  const handleGeneratePublicApiKey = async () => {
    try {
      setIsGeneratingPublicApiKey(true);
      setApiKeyError(null);
      const response = await api(() => publicApiKeyService.generateApiKey(companyId));
      publicApiKeys.setData((prev) => [...prev, response.data]);
    } catch (err) {
      setApiKeyError(serverMessage(err, 'Could not generate a public API key'));
      console.error(err);
    } finally {
      setIsGeneratingPublicApiKey(false);
    }
  };

  const handleDeletePublicApiKey = async (key) => {
    const last4 = String(key.apiKey || '').slice(-4);
    if (!(await confirm({
      title: `Delete the API key ending ${last4}?`,
      body: 'Any integration using this key stops working straight away. This cannot be undone.',
      confirmLabel: 'Delete key',
      variant: 'danger',
    }))) return;
    try {
      setDeletingKeyId(key.id);
      setApiKeyError(null);
      await api(() => publicApiKeyService.deleteApiKey(companyId, key.id));
      publicApiKeys.setData((prev) => prev.filter((k) => k.id !== key.id));
    } catch (err) {
      setApiKeyError(serverMessage(err, 'Could not delete the public API key'));
      console.error(err);
    } finally {
      setDeletingKeyId(null);
    }
  };

  // Only a changed tenant ID is checked, so an old stored value never blocks saving the name
  const tenantId = nameSignInForm.draft.azureTenantId;
  const tenantIdInvalid = tenantId !== '' && tenantId !== (company.azureTenantId || '') && !GUID.test(tenantId);
  const nameMissing = nameSignInForm.draft.name.trim() === '';

  const showPublicApiKeys = !publicApiFlagKnown || publicApiOn;

  return (
    <>
      {confirmDialog}

      <ConfigCard id="config-signin" title="Name and sign-in">
        <SettingsForm form={nameSignInForm} canEdit={canEdit} invalid={tenantIdInvalid || nameMissing}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="companyName">
                <Form.Label>Company name</Form.Label>
                <Form.Control
                  type="text"
                  value={nameSignInForm.draft.name}
                  onChange={(e) => nameSignInForm.setField('name', e.target.value)}
                  placeholder="Company name"
                  isInvalid={nameMissing}
                />
                <Form.Control.Feedback type="invalid">Enter a company name.</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="azureTenantId">
                <Form.Label>Azure tenant ID</Form.Label>
                <Form.Control
                  type="text"
                  value={nameSignInForm.draft.azureTenantId}
                  onChange={(e) => nameSignInForm.setField('azureTenantId', e.target.value.trim())}
                  placeholder="e.g. 72f988bf-86f1-41af-91ab-2d7cd011db47"
                  isInvalid={tenantIdInvalid}
                  aria-describedby="azureTenantIdHelp"
                />
                <Form.Control.Feedback type="invalid">
                  A tenant ID is a GUID, such as 72f988bf-86f1-41af-91ab-2d7cd011db47.
                </Form.Control.Feedback>
                <Form.Text id="azureTenantIdHelp" className="text-muted">
                  The directory (tenant) ID of the company&apos;s Microsoft Entra directory.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
          <SwitchField
            id="tenantBasedAuthEnabled"
            label="Tenant-based sign-in"
            help="Anyone who signs in with a Microsoft account from this tenant joins the company automatically, while it has seats left."
            checked={nameSignInForm.draft.tenantBasedAuthEnabled}
            onChange={(value) => nameSignInForm.setField('tenantBasedAuthEnabled', value)}
          />
        </SettingsForm>
      </ConfigCard>

      <ConfigCard id="config-signup" title="Sign-up">
        <SettingsForm form={signupForm} canEdit={canEdit}>
          <SwitchField
            id="domainNameSignupsEnabled"
            label="Domain sign-ups"
            help="New sign-ups with an email on an approved sign-up domain below join this company automatically. While this is off, no domain takes effect."
            checked={signupForm.draft.domainNameSignupsEnabled}
            onChange={(value) => signupForm.setField('domainNameSignupsEnabled', value)}
          />
        </SettingsForm>

        <hr className="my-4" />

        {/* Domain-based sign-up: domains raised by the company and approved by staff */}
        <h6 className="mb-2">Sign-up domains</h6>
        <p className="text-muted small mb-2">
          Email domains whose new sign-ups auto-join this company. A row only takes effect once approved and while domain sign-ups are on.
        </p>
        <Alert variant="warning" className="small py-2 mb-3">
          An approved domain puts anyone with that email straight into the account, so check the company owns it first.
        </Alert>
        <DomainList
          idPrefix="signup"
          {...domainListProps('signup')}
          addLabel="Add a sign-up domain"
          addButtonLabel="Add sign-up domain"
          loadingLabel="Loading sign-up domains"
          errorText="Could not load the sign-up domains."
          emptyText="No sign-up domains yet."
        />

        <hr className="my-4" />

        {/* Locked sign-up domains: raised by the company, approved by staff */}
        <h6 className="mb-2">Locked sign-up domains</h6>
        <p className="text-muted small mb-2">
          Domains the company has asked us to stop new accounts from. A row only blocks sign-ups once approved.
        </p>
        <Alert variant="warning" className="small py-2 mb-3">Check the company owns the domain first.</Alert>
        <DomainList
          idPrefix="locked"
          {...domainListProps('locked')}
          addLabel="Add a locked domain"
          addButtonLabel="Add locked domain"
          loadingLabel="Loading locked domains"
          errorText="Could not load the locked sign-up domains."
          emptyText="No locked sign-up domains yet."
        />
      </ConfigCard>

      <ConfigCard id="config-limits" title="Limits">
        <SettingsForm form={limitsForm} canEdit={canEdit}>
          <p className="text-muted small">The most teams, tags and meeting rooms the company can create. 0 means it cannot create any.</p>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3" controlId="numberOfTeamsLimit">
                <Form.Label>Teams</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="1"
                  value={limitsForm.draft.numberOfTeamsLimit}
                  onChange={(e) => limitsForm.setField('numberOfTeamsLimit', limitValue(e.target.value))}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3" controlId="numberOfTagsLimit">
                <Form.Label>Tags</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="1"
                  value={limitsForm.draft.numberOfTagsLimit}
                  onChange={(e) => limitsForm.setField('numberOfTagsLimit', limitValue(e.target.value))}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3" controlId="meetingRoomLimit">
                <Form.Label>Meeting rooms</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="1"
                  value={limitsForm.draft.meetingRoomLimit}
                  onChange={(e) => limitsForm.setField('meetingRoomLimit', limitValue(e.target.value))}
                />
              </Form.Group>
            </Col>
          </Row>
        </SettingsForm>
      </ConfigCard>

      <ConfigCard id="config-scim" title="SCIM">
        <SettingsForm form={scimForm} canEdit={canEdit}>
          <SwitchField
            id="deleteWhenSoftDeleted"
            label="Delete users that SCIM deactivates"
            help="On: a user deactivated in the customer's identity provider is deleted from Team Today. Off: they stay, with their access denied."
            checked={scimForm.draft.deleteWhenSoftDeleted}
            onChange={(value) => scimForm.setField('deleteWhenSoftDeleted', value)}
          />
        </SettingsForm>

        <hr className="my-4" />

        <h6 className="mb-3">SCIM API keys</h6>
        {scimError && <Alert variant="danger" dismissible onClose={() => setScimError(null)} className="mb-3">{scimError}</Alert>}
        <ListState
          list={scimApiKeys}
          loadingLabel="Loading SCIM API keys"
          errorText="Could not load the SCIM API keys."
          emptyText="No SCIM API keys for this company."
        >
          <StackedTable size="sm" responsive="xl" className="mb-0">
            <thead>
              <tr>
                <th>API key</th>
                <th>Label</th>
                <th style={{ width: 110 }}>Status</th>
                {canEdit && <th style={{ width: 140 }}><span className="visually-hidden">Actions</span></th>}
              </tr>
            </thead>
            <tbody>
              {scimApiKeys.data.map((key) => (
                <tr key={key.id}>
                  <td><MaskedKey value={key.apiKey} canReveal={canEdit} /></td>
                  <td>{key.label || <span className="text-muted">{EMPTY}</span>}</td>
                  <td>{key.locked ? <Badge bg="danger">Locked</Badge> : <span className="text-muted">Unlocked</span>}</td>
                  {canEdit && (
                    <td>
                      {key.locked && (
                        <BusyButton
                          variant="outline-secondary"
                          size="sm"
                          disabled={unlockingScimKeyId === key.id}
                          busy={unlockingScimKeyId === key.id}
                          busyLabel="Unlocking..."
                          onClick={() => handleUnlockScimApiKey(key.id)}
                        >
                          Unlock
                        </BusyButton>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </StackedTable>
        </ListState>
      </ConfigCard>

      <ConfigCard id="config-checkin" title="Office check-in">
        <SettingsForm form={checkinForm} canEdit={canEdit}>
          <SwitchField
            id="automaticOfficeCheckinEnabled"
            label="Automatic office check-in"
            help="Records people as in the office when the check-in agent reports them from an approved IP address below."
            checked={checkinForm.draft.automaticOfficeCheckinEnabled}
            onChange={(value) => checkinForm.setField('automaticOfficeCheckinEnabled', value)}
          />
        </SettingsForm>

        <hr className="my-4" />

        {!checkinOn ? (
          <p className="text-muted mb-0">
            Automatic check-in is off, so check-in IP addresses are not used. Turn it on and save to manage them.
          </p>
        ) : (
          <>
            <h6 className="mb-2">Check-in IP addresses</h6>
            <p className="text-muted small">
              Customers request addresses from their office settings. Only approve an address after checking it belongs to the
              customer or their internet provider (Check owner). Never approve cloud Wi-Fi or security gateway addresses
              (Zscaler, Netskope, Cloudflare, Prisma Access, AWS, Azure, Google Cloud).
            </p>
            {ipAddressError && (
              <Alert variant="danger" dismissible onClose={() => setIpAddressError(null)} className="mb-3">{ipAddressError}</Alert>
            )}
            <ListState
              list={ipAddresses}
              loadingLabel="Loading check-in IP addresses"
              errorText="Could not load the check-in IP addresses."
              emptyText="No check-in IP addresses yet, so every check-in for this company is ignored."
            >
              <StackedTable size="sm" responsive className="mb-3">
                <thead>
                  <tr>
                    <th>IP address</th>
                    <th>Office</th>
                    <th>Status</th>
                    <th className="d-md-none d-xl-table-cell">Label</th>
                    <th>Owner</th>
                    <th className="d-md-none d-xl-table-cell">Requested or added by</th>
                    <th className="d-md-none d-xl-table-cell">Date</th>
                    {canEdit && <th><span className="visually-hidden">Actions</span></th>}
                  </tr>
                </thead>
                <tbody>
                  {ipAddresses.data.map((row) => (
                    <tr key={row.id}>
                      <td><code>{row.ipAddress}</code></td>
                      <td>{row.officeName || <span className="text-muted">{EMPTY}</span>}</td>
                      <td>
                        <Badge bg={statusBg(row.status)}>{IP_ADDRESS_STATUS_LABELS[row.status] || statusLabel(row.status)}</Badge>
                        {row.decidedBy && <div className="text-muted small">by {row.decidedBy}</div>}
                      </td>
                      <td className="d-md-none d-xl-table-cell">{row.label || <span className="text-muted">{EMPTY}</span>}</td>
                      <td>{renderIpOwner(row)}</td>
                      <td className="d-md-none d-xl-table-cell">
                        {row.requestedByEmail || row.requestedByUserId || (row.createdBy
                          ? <>{row.createdBy} <span className="text-muted small">(staff)</span></>
                          : <span className="text-muted">{EMPTY}</span>)}
                      </td>
                      <td className="text-nowrap d-md-none d-xl-table-cell">{formatDate(row.createdAt)}</td>
                      {canEdit && (
                        <td className="text-end text-nowrap">
                          {row.status === 'PENDING' && (
                            <>
                              <BusyButton
                                variant="outline-primary"
                                size="sm"
                                className="me-2"
                                disabled={isIpBusy(row)}
                                busy={isIpBusy(row, 'approve')}
                                busyLabel="Approving..."
                                onClick={() => handleDecideIpAddress(row, 'approve')}
                              >
                                Approve
                              </BusyButton>
                              <BusyButton
                                variant="outline-danger"
                                size="sm"
                                className="me-2"
                                disabled={isIpBusy(row)}
                                busy={isIpBusy(row, 'reject')}
                                busyLabel="Rejecting..."
                                onClick={() => handleDecideIpAddress(row, 'reject')}
                              >
                                Reject
                              </BusyButton>
                            </>
                          )}
                          <BusyButton
                            variant="outline-secondary"
                            size="sm"
                            disabled={isIpBusy(row)}
                            busy={isIpBusy(row, 'remove')}
                            busyLabel="Removing..."
                            onClick={() => handleRemoveIpAddress(row)}
                          >
                            Remove
                          </BusyButton>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </StackedTable>
              {!ipAddresses.data.some((row) => row.status === 'APPROVED') && (
                <p className="text-muted small mb-3">No address is approved yet, so every check-in for this company is ignored.</p>
              )}
            </ListState>
            {canEdit && (
              <>
                <Row className="mb-3 align-items-end gy-2 gy-md-0">
                  <Col md={3}>
                    <Form.Group controlId="newIpAddress">
                      <Form.Label>IP address</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="e.g. 203.0.113.10"
                        value={newIpAddress}
                        onChange={(e) => { setNewIpAddress(e.target.value); setIpAddressCheck(null); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCheckIpAddress(); } }}
                        disabled={isAddingIpAddress}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group controlId="newIpAddressLabel">
                      <Form.Label>Label</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="e.g. London office"
                        maxLength={255}
                        value={newIpAddressLabel}
                        onChange={(e) => setNewIpAddressLabel(e.target.value)}
                        disabled={isAddingIpAddress}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group controlId="newIpAddressOfficeId">
                      <Form.Label>Office (optional)</Form.Label>
                      <Form.Select
                        value={newIpAddressOfficeId}
                        onChange={(e) => setNewIpAddressOfficeId(e.target.value)}
                        disabled={isAddingIpAddress}
                      >
                        <option value="">No specific office</option>
                        {offices.data.map((office) => (
                          <option key={office.id} value={office.id}>{office.label}</option>
                        ))}
                      </Form.Select>
                      {offices.error && (
                        <Form.Text className="text-danger d-block">
                          Could not load the offices.{' '}
                          <Button variant="link" size="sm" className="p-0 align-baseline" onClick={offices.reload}>Try again</Button>
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md="auto">
                    <BusyButton
                      variant="outline-secondary"
                      className="me-2"
                      onClick={handleCheckIpAddress}
                      disabled={isCheckingIpAddress || isAddingIpAddress || !newIpAddress.trim()}
                      busy={isCheckingIpAddress}
                      busyLabel="Checking..."
                    >
                      Check
                    </BusyButton>
                    <BusyButton
                      variant="outline-primary"
                      onClick={handleAddIpAddress}
                      disabled={!ipAddressCheckIsCurrent || isCheckingIpAddress || isAddingIpAddress}
                      busy={isAddingIpAddress}
                      busyLabel="Adding..."
                    >
                      Add
                    </BusyButton>
                  </Col>
                  <Col xs={12}>
                    <Form.Text className="text-muted">Added here as approved immediately. Check the address first to see its registered owner.</Form.Text>
                  </Col>
                </Row>
                {ipAddressCheckIsCurrent && (
                  <div className="border rounded p-2 mb-3 small">
                    <div>Address to add: <code>{ipAddressCheck.ipAddress}</code></div>
                    <div>Registered owner: <strong>{ipAddressCheck.owner}</strong></div>
                    <div className="text-muted">Check the owner is the customer or their internet provider before adding.</div>
                  </div>
                )}
                {ipAddressCheckIsCurrent && ipAddressCheck.otherCompanies?.length > 0 && (
                  <Alert variant="warning" className="mb-3">
                    <code>{ipAddressCheck.ipAddress}</code> is already listed for{' '}
                    {ipAddressCheck.otherCompanies.map((other) => other.companyName || other.companyId).join(', ')}.
                    An address shared by more than one company is often a shared building, co-working space or security gateway.
                    Confirm with the customer that it is theirs before adding it.
                  </Alert>
                )}
              </>
            )}

            <h6 className="mb-2 mt-4">Unlisted IPs seen (last 14 days)</h6>
            <p className="text-muted small">
              Requests from addresses on no allowlist that named one of this company&apos;s users. Anyone can send these,
              so only allow an address once the customer confirms it is theirs.
            </p>
            <ListState
              list={unlistedIps}
              loadingLabel="Loading unlisted IPs"
              errorText="Could not load the unlisted IPs."
              emptyText="No requests from unlisted IPs."
            >
              <StackedTable size="sm" responsive className="mb-3">
                <thead>
                  <tr>
                    <th>IP</th>
                    <th>Day</th>
                    <th>Requests</th>
                    <th>Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {unlistedIps.data.map((row) => (
                    <tr key={row.id}>
                      <td><code>{row.ip}</code></td>
                      <td className="text-nowrap">{formatDay(row.day)}</td>
                      <td>{formatNumber(row.requestCount)}</td>
                      <td className="text-nowrap">{formatDateTime(row.lastSeenAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </StackedTable>
            </ListState>

            <h6 className="mb-2 mt-4">Ignored check-ins from approved addresses (last 14 days)</h6>
            <p className="text-muted small">
              Requests from this company&apos;s approved addresses that could not be recorded. The company&apos;s admins see
              the same list, with what to fix, on their automatic check-in report.
            </p>
            <ListState
              list={ignoredCheckins}
              loadingLabel="Loading ignored check-ins"
              errorText="Could not load the ignored check-ins."
              emptyText="No ignored check-ins."
            >
              <StackedTable size="sm" responsive className="mb-0">
                <thead>
                  <tr>
                    <th>Day</th>
                    <th>Reason</th>
                    <th>Detail</th>
                    <th>Office</th>
                    <th>Requests</th>
                    <th>Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {ignoredCheckins.data.map((row) => (
                    <tr key={row.id}>
                      <td className="text-nowrap">{formatDay(row.day)}</td>
                      <td>{IGNORED_CHECKIN_REASONS[row.reason] || statusLabel(row.reason)}</td>
                      <td>{row.detail ? <code>{row.detail}</code> : EMPTY}</td>
                      <td>{(row.officeId && offices.data.find((office) => office.id === row.officeId)?.label) || EMPTY}</td>
                      <td>{formatNumber(row.requestCount)}</td>
                      <td className="text-nowrap">{formatDateTime(row.lastSeenAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </StackedTable>
            </ListState>
          </>
        )}
      </ConfigCard>

      <ConfigCard id="config-public-api" title="Public API">
        {publicApiFlagKnown ? (
          <SettingsForm form={publicApiForm} canEdit={canEdit}>
            <SwitchField
              id="publicApiAccessEnabled"
              label="Public API access"
              help="Lets the company call the Team Today public API with the keys below. While it is off, every key is refused."
              checked={publicApiForm.draft.publicApiAccessEnabled}
              onChange={(value) => publicApiForm.setField('publicApiAccessEnabled', value)}
            />
          </SettingsForm>
        ) : (
          <SwitchField
            id="publicApiAccessEnabled"
            label="Public API access"
            help="The admin API does not return or save this setting yet, so it cannot be read or changed here."
            checked={false}
            disabled
            onChange={() => {}}
          />
        )}

        <hr className="my-4" />

        <h6 className="mb-3">API keys</h6>
        {apiKeyError && <Alert variant="danger" dismissible onClose={() => setApiKeyError(null)} className="mb-3">{apiKeyError}</Alert>}
        {!showPublicApiKeys ? (
          <p className="text-muted mb-0">
            Public API access is off, so no key works.
            {publicApiKeys.loaded && publicApiKeys.data.length > 0 && ` ${publicApiKeys.data.length === 1 ? 'The 1 existing key starts' : `The ${formatNumber(publicApiKeys.data.length)} existing keys start`} working again if it is turned back on.`}
          </p>
        ) : (
          <>
            <ListState
              list={publicApiKeys}
              loadingLabel="Loading API keys"
              errorText="Could not load the public API keys."
              emptyText="No API keys generated yet."
            >
              <StackedTable size="sm" responsive="xl" className="mb-3">
                <thead>
                  <tr>
                    <th>API key</th>
                    {canEdit && <th style={{ width: 110 }}><span className="visually-hidden">Actions</span></th>}
                  </tr>
                </thead>
                <tbody>
                  {publicApiKeys.data.map((key) => (
                    <tr key={key.id}>
                      <td><MaskedKey value={key.apiKey} canReveal={canEdit} /></td>
                      {canEdit && (
                        <td>
                          <BusyButton
                            variant="outline-danger"
                            size="sm"
                            disabled={deletingKeyId === key.id}
                            busy={deletingKeyId === key.id}
                            busyLabel="Deleting..."
                            onClick={() => handleDeletePublicApiKey(key)}
                          >
                            Delete
                          </BusyButton>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </StackedTable>
            </ListState>
            {canEdit && (
              <BusyButton
                variant="outline-primary"
                size="sm"
                onClick={handleGeneratePublicApiKey}
                disabled={isGeneratingPublicApiKey}
                busy={isGeneratingPublicApiKey}
                busyLabel="Generating..."
              >
                Generate API key
              </BusyButton>
            )}
          </>
        )}
      </ConfigCard>
    </>
  );
};

export default CompanyConfig;
